import { Utilities } from 'core/domains';
import { select } from 'core/middlewares/utilities';
import { getAccessToken } from 'core/services/auth';
import { utilities } from 'core/use-cases';
import { getLocationPathname } from 'core/utilities';
import { matchPath } from 'react-router';

import type { RootState } from 'core/store';

/**
 * Has the access token from the store
 *
 */
export function hasAccessToken() {
  return !!getAccessToken();
}

/**
 * Get the homepage from store settings/routes entry
 *
 */
export function getHomepageRoute() {
  return '/departamentos';
}

export type RoutePath = RootState['settings']['data']['routes'][number]['path'];

/**
 * Get the isPublic from location.pathname
 *
 */
export function* isPublicRoute() {
  const routes: RootState['settings']['data']['routes'] = yield select(
    ({ settings }: RootState) => settings.data.routes,
  );

  const { isPublic } =
    routes?.find(({ path }) => {
      const match = matchPath(getLocationPathname(), path);

      return !!match;
    }) || {};

  return isPublic || false;
}

/**
 * Get the status from settings
 *
 */
export function* getSettingsStatus() {
  const status: RootState['settings']['status'] = yield select(
    ({ settings }: RootState) => settings.status,
  );

  return status;
}

/**
 * Get user login status
 *
 */
export function* isUserLogged() {
  const userStatus: RootState['user']['status'] = yield select(
    ({ user }: RootState) => user.status,
  );

  return userStatus === Utilities.STATUS.SUCCESS;
}

/**
 * Get the status of fetch from payment methods
 *
 */
export function* getPaymentMethodsStatus() {
  const status: RootState['checkout']['paymentMethods']['status'] = yield select(
    ({ checkout }: RootState) => checkout.paymentMethods.status,
  );

  return status;
}

/**
 * Get data cart context
 *
 */
export function* getCart() {
  const data: RootState['cart'] = yield select(({ cart }: RootState) => cart);

  return data;
}

export type Cart = RootState['cart'];

/**
 * Get the isPrivate from store settings/routes
 *
 */
export function* getRouteConfig() {
  const routes: RootState['settings']['data']['routes'] = yield select(
    ({ settings }: RootState) => settings.data.routes,
  );

  const { isPrivate, canBlockedResellerAccess } =
    routes.find(({ path }) => utilities.isExactRouter(path)) || {};

  return {
    isPrivate: isPrivate || true,
    canBlockedResellerAccess: canBlockedResellerAccess || false,
  };
}

/**
 * Is path initial of application if path is equal "/"
 *
 */
export function* isInitRoute() {
  const rootState: RootState = yield select();
  const isExact = utilities.isExactRouter('/');

  return isExact;
}

export type SettingsStatus = RootState['settings']['status'];

/**
 * Get the data of fetch from payment methods
 *
 */
export function* getPaymentMethods() {
  const data: RootState['checkout']['paymentMethods'] = yield select(
    ({ checkout }: RootState) => checkout.paymentMethods,
  );

  return data;
}

export type PaymentMethods = RootState['checkout']['paymentMethods'];

/**
 * Get the data of fetch from payment options
 *
 */
export function* getPaymentOption() {
  const data: RootState['checkout']['paymentOption'] = yield select(
    ({ checkout }: RootState) => checkout.paymentOption,
  );

  return data;
}

export type PaymentOption = RootState['checkout']['paymentOption'];

/**
 * Get the path of page search
 *
 */
export function* getPathSearch() {
  const {
    queryOptions: { query },
  }: RootState['catalog'] = yield select(({ catalog }: RootState) => catalog);

  const { routes }: RootState['settings']['data'] = yield select(
    ({ settings }: RootState) => settings.data,
  );

  const { path } = routes.find(({ isSearchPage }) => isSearchPage) || {};

  return `${path}?q=${query}`;
}

/**
 * get the combo path
 *
 */
export function* getPathCombo() {
  const {
    comboEditConfirmation: { comboCode },
  }: RootState['cart'] = yield select(({ cart }: RootState) => cart);

  return `/combo?q=${comboCode}`;
}

/**
 * Get data order context
 *
 */
export function* getOrder() {
  const data: RootState['order'] = yield select(({ order }: RootState) => order);

  return data;
}

/**
 * Get data checkout context
 *
 */
export function* getCheckout() {
  const data: RootState['order'] = yield select(({ checkout }: RootState) => checkout);

  return data;
}

/**
 * Get data user context
 *
 */
export function* getUser() {
  const data: RootState['user']['data'] = yield select(({ user }: RootState) => user.data);

  return data;
}

export type UserState = RootState['user']['data'];
